import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';
import { toApiError } from '@shared/api/errors';
import { useAuthStore } from '@shared/stores/authStore';
import { fetchProfile } from '@features/profile/api';

/**
 * Events — canonical member-facing routes (`/events*`, `tenantMiddleware`).
 * RSVP is `POST /events/:id/register` (members register; there is no
 * `/rsvp` endpoint). Responses use the `{ success, data }` envelope.
 *
 * The tenant API returns `name`, `start_date`, `location_name`, etc.
 * This module maps those fields to the resident-app `EventSummary` shape.
 */

export interface EventGuestSummary {
  guest_name?: string | null;
  guest_email?: string | null;
  approval_status?: string | null;
}

export interface EventAttachmentSummary {
  event_attachment_id: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
}

export interface EventSummary {
  event_id: string;
  title: string;
  starts_at: string;
  ends_at?: string | null;
  all_day?: boolean;
  location?: string;
  cover_image_url?: string;
  image_url?: string;
  cover_image_key?: string;
  image_key?: string;
  category?: string;
  description?: string;
  rsvp_required?: boolean;
  rsvp_cutoff_at?: string | null;
  max_attendees?: number | null;
  current_attendees?: number;
  /** Seats left, as the server counts them. Null when the event has no limit. */
  seats_remaining?: number | null;
  allow_guests?: boolean;
  max_guests?: number | null;
  approval_status?: string | null;
  rsvp_guests?: EventGuestSummary[];
  rsvp_status?: 'yes' | 'maybe' | 'no' | null;
  organiser_name?: string | null;
  organiser_email?: string | null;
  organiser_phone?: string | null;
  /** True when this event is one occurrence of a repeating series. */
  is_recurring?: boolean;
  attachments?: EventAttachmentSummary[];
}

export interface EventDetail extends EventSummary {
  description?: string;
  organizer_name?: string;
  capacity?: number;
  attending_count?: number;
}

/** Raw event row from GET /events (paginated or list). */
interface RawEventRow {
  event_id: string;
  name?: string;
  title?: string;
  start_date?: string | Date;
  starts_at?: string;
  end_date?: string | Date | null;
  ends_at?: string | null;
  start_time?: string;
  end_time?: string;
  location_name?: string;
  location?: string;
  event_type_name?: string;
  category?: string;
  description?: string;
  cover_image_url?: string;
  image_url?: string;
  allow_guests?: boolean;
  max_guests?: number | null;
  approval_status?: string | null;
  rsvp_guests?: EventGuestSummary[];
  is_rsvp?: boolean;
  registration_status?: string;
  rsvp_status?: 'yes' | 'maybe' | 'no' | null;
  max_attendees?: number;
  current_attendees?: number;
  seats_remaining?: number | null;
  rsvp_required?: boolean;
  rsvp_cutoff_at?: string | null;
  organiser_name?: string | null;
  organiser_email?: string | null;
  organiser_phone?: string | null;
  recurrence_rule?: unknown;
  recurrence_parent_id?: string | null;
  attachments?: EventAttachmentSummary[];
}

function toIsoTimestamp(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

/** RSVP closes at the cutoff instant, whatever the device's time zone. */
export function isRsvpCutoffReached(event: Pick<EventSummary, 'rsvp_required' | 'rsvp_cutoff_at'>): boolean {
  if (!event.rsvp_required || !event.rsvp_cutoff_at) return false;
  return Date.now() >= new Date(event.rsvp_cutoff_at).getTime();
}

/**
 * Whether the event is full. The server is the authority — it counts members plus their guests and
 * refuses an RSVP that would overshoot — so `seats_remaining` is preferred when present, with the
 * attendee counts as the fallback for an older response shape.
 */
export function isRsvpCapacityReached(
  event: Pick<EventSummary, 'max_attendees' | 'current_attendees' | 'seats_remaining'>,
): boolean {
  if (event.seats_remaining != null) return event.seats_remaining <= 0;
  if (event.max_attendees == null || event.max_attendees <= 0) return false;
  return (event.current_attendees ?? 0) >= event.max_attendees;
}

export function isRsvpClosed(event: EventSummary): boolean {
  return isRsvpCutoffReached(event) || isRsvpCapacityReached(event);
}

function mapRsvpStatus(raw: RawEventRow): 'yes' | 'maybe' | 'no' | null {
  if (raw.approval_status === 'declined' || raw.registration_status === 'declined') {
    return 'no';
  }
  if (raw.rsvp_status === 'yes' || raw.rsvp_status === 'maybe' || raw.rsvp_status === 'no') {
    return raw.rsvp_status;
  }
  if (raw.is_rsvp || raw.registration_status === 'registered' || raw.registration_status === 'pending_payment' || raw.registration_status === 'pending_approval') {
    return 'yes';
  }
  return null;
}

function currentMemberId(): string | undefined {
  const user = useAuthStore.getState().user;
  return user?.member_id;
}

async function resolveCurrentMemberId(): Promise<string | undefined> {
  const existingMemberId = currentMemberId();
  if (existingMemberId) return existingMemberId;

  try {
    const profile = await fetchProfile();
    return profile.member_id;
  } catch {
    return undefined;
  }
}

function isAllDayEvent(raw: RawEventRow, startsAt: string): boolean {
  if (raw.start_time === '00:00' && (!raw.end_time || raw.end_time === '00:00')) {
    return true;
  }
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return false;
  return start.getHours() === 0 && start.getMinutes() === 0;
}

export function mapEventRow(raw: RawEventRow): EventSummary | null {
  const starts_at = toIsoTimestamp(raw.starts_at ?? raw.start_date);
  if (!starts_at) return null;

  const ends_at = toIsoTimestamp(raw.ends_at ?? raw.end_date) ?? null;
  const description = typeof raw.description === 'string' ? raw.description.trim() : '';

  return {
    event_id: raw.event_id,
    title: (raw.title ?? raw.name ?? '').trim() || 'Event',
    starts_at,
    ends_at,
    all_day: isAllDayEvent(raw, starts_at),
    location: raw.location ?? raw.location_name ?? undefined,
    cover_image_url: raw.cover_image_url ?? raw.image_url,
    category: raw.category ?? raw.event_type_name ?? undefined,
    description: description || undefined,
    rsvp_required: raw.rsvp_required === true,
    rsvp_cutoff_at: toIsoTimestamp(raw.rsvp_cutoff_at) ?? null,
    max_attendees: raw.max_attendees ?? null,
    current_attendees: raw.current_attendees ?? 0,
    seats_remaining: raw.seats_remaining ?? null,
    allow_guests: raw.allow_guests === true,
    max_guests: raw.max_guests ?? null,
    approval_status: raw.approval_status ?? null,
    rsvp_guests: raw.rsvp_guests ?? [],
    rsvp_status: mapRsvpStatus(raw),
    organiser_name: raw.organiser_name ?? null,
    organiser_email: raw.organiser_email ?? null,
    organiser_phone: raw.organiser_phone ?? null,
    is_recurring: Boolean(raw.recurrence_rule || raw.recurrence_parent_id),
    attachments: raw.attachments ?? [],
  };
}

export async function fetchEvents(
  rangeStart?: string,
  rangeEnd?: string,
  memberId?: string,
): Promise<EventSummary[]> {
  const scopedMemberId = memberId ?? await resolveCurrentMemberId();
  // The API returns only published events this member's audience covers, so no client-side filtering.
  const { data } = await tenantApi.get('/events', {
    params: {
      page: 1,
      limit: 100,
      start_date: rangeStart,
      end_date: rangeEnd,
      ...(scopedMemberId ? { member_id: scopedMemberId } : {}),
    },
  });
  const rows = unwrapList<RawEventRow>(data);
  return rows
    .map(mapEventRow)
    .filter((e): e is EventSummary => e !== null);
}

/** The events feed and My Events: five years back (ended events stay listed) to six months ahead. */
export function fetchEventFeed(): Promise<EventSummary[]> {
  const from = new Date();
  from.setFullYear(from.getFullYear() - 5);
  const to = new Date();
  to.setMonth(to.getMonth() + 6);
  return fetchEvents(from.toISOString(), to.toISOString());
}

export async function fetchEvent(eventId: string, memberId?: string): Promise<EventDetail> {
  const scopedMemberId = memberId ?? await resolveCurrentMemberId();
  const { data } = await tenantApi.get(`/events/${eventId}`, {
    params: scopedMemberId ? { member_id: scopedMemberId } : undefined,
  });
  const raw = unwrap<RawEventRow>(data);
  const summary = mapEventRow(raw);
  if (!summary) {
    throw new Error('Event has no valid start date');
  }
  return {
    ...summary,
    description: raw.description,
    capacity: raw.max_attendees,
    attending_count: raw.current_attendees,
  };
}

/** Downloads one of an event's attachments. The API authorises the read through the event. */
export async function downloadEventAttachment(
  eventId: string,
  attachmentId: string,
): Promise<Blob> {
  const { data } = await tenantApi.get(`/events/${eventId}/attachments/${attachmentId}`, {
    responseType: 'blob',
  });
  return data as Blob;
}

export interface RsvpRegisterResult {
  approval_status?: string | null;
}

/** Thrown when the event filled up before this RSVP landed. */
export class EventFullError extends Error {}

/**
 * RSVP to an event. The canonical route registers the member; the legacy
 * `status` argument is mapped to the canonical member registration flow.
 *
 * A decline is recorded server-side rather than deleting the registration, so it holds across
 * devices and admins can message the members who said no.
 */
export async function rsvpEvent(
  eventId: string,
  status: 'yes' | 'no',
  guests: Array<{ name: string; email: string }> = [],
): Promise<RsvpRegisterResult | undefined> {
  // The decline route reads the member from the session, so it needs no profile lookup.
  if (status === 'no') {
    await tenantApi.post(`/events/${eventId}/decline`, {});
    return undefined;
  }

  const memberId = await resolveCurrentMemberId();
  if (!memberId) {
    throw new Error('No member profile found for RSVP.');
  }

  try {
    const { data } = await tenantApi.post(`/events/${eventId}/register`, {
      member_id: memberId,
      ...(guests.length > 0 ? { guests } : {}),
    });
    const registration = unwrap<RsvpRegisterResult>(data);
    return { approval_status: registration.approval_status ?? null };
  } catch (error) {
    // The server is the authority on capacity, and it can fill between render and submit.
    if (toApiError(error).code === 'EVENT_CAPACITY_REACHED') {
      throw new EventFullError();
    }
    throw error;
  }
}
